import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { OktaAuth } from '@okta/okta-auth-js';
import { getDelegatedUsers } from '../services/apiClient';
import {
  acceptInjectedJwt,
  createOktaAuth,
  getToken,
  initLogin,
  login as oktaLogin,
  logout as oktaLogout,
  validateAccessToken,
} from '../services/okta';
import { loadRuntimeConfig } from '../services/runtimeConfig';
import { resetTokenResolver, setTokenResolver } from '../services/tokenProvider';
import { getIdToken, getLoggedUserDetails, setAccessToken } from '../utils/authStorage';
import { setLoggedUserDetails } from '../utils/authStorage';
import { toErrorState } from '../utils/errorMessage';
import type { AuthConfig, DelegatedUser, ErrorState, LoggedUser } from '../types';

/**
 * The auth half of the old `App.js`.
 *
 * App.js owned `isAuthenticated`, `isLoading`, `loggedUserDetails`, `delegatedUsers`,
 * `loggedUserGteEmployeeNo` and `operationalUserGteEmployeeNo`, bootstrapped them in
 * `componentDidMount`, and passed them down to NavBar and MainMenu. That bootstrap is
 * reproduced here; the one addition is the `/api/auth-config` fetch that has to complete
 * first, because the old app read the same values from a bundled JSON import.
 */

export interface AuthContextValue {
  config: AuthConfig | null;
  oktaAuth: OktaAuth | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  loggedUserDetails: LoggedUser | null;
  delegatedUsers: DelegatedUser[];
  loggedUserGteEmployeeNo: number;
  operationalUserGteEmployeeNo: number;
  error: ErrorState;
  clearError: () => void;
  /** NavBar's delegated-user switch (App.js:260-270). */
  selectDelegatedUser: (operational: number, logged: number) => void;
  logIn: () => void;
  logOut: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const NO_ERROR: ErrorState = { status: false, message: '' };

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [config, setConfig] = useState<AuthConfig | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loggedUserDetails, setLoggedUserDetailsState] = useState<LoggedUser | null>(null);
  const [delegatedUsers, setDelegatedUsers] = useState<DelegatedUser[]>([]);
  const [loggedUserGteEmployeeNo, setLoggedUserGteEmployeeNo] = useState(0);
  const [operationalUserGteEmployeeNo, setOperationalUserGteEmployeeNo] = useState(0);
  const [error, setError] = useState<ErrorState>(NO_ERROR);

  const oktaRef = useRef<OktaAuth | null>(null);
  /**
   * StrictMode invokes effects twice in development. The old app's componentDidMount ran
   * once, and running this bootstrap twice would issue a second Okta redirect and a
   * duplicate delegated-users request. The ref makes the second invocation a no-op.
   */
  const bootstrappedRef = useRef(false);

  /** App.js:144-170 getDelegatedUserList — including its `user === null` guard. */
  const loadDelegatedUsers = useCallback((user: string | null | undefined) => {
    if (user === null || user === undefined) return;

    getDelegatedUsers(user)
      .then((response) => {
        // The old app assigned response.data.delegatedUsers straight into state, which
        // could be null; render() then guarded with `!== null` before spreading. Keeping
        // the array non-null here and letting the guard live in one place is the same
        // rendered output, without the possibility of a null spread.
        setDelegatedUsers(response?.delegatedUsers ?? []);
        setLoggedUserGteEmployeeNo(response?.gteEmployeeNo);
        setOperationalUserGteEmployeeNo(response?.gteEmployeeNo);
      })
      .catch((err) => {
        setError(toErrorState(err));
      });
  }, []);

  useEffect(() => {
    if (bootstrappedRef.current) return;
    bootstrappedRef.current = true;

    let cancelled = false;

    void (async () => {
      try {
        const loaded = await loadRuntimeConfig();
        if (cancelled) return;

        const oktaAuth = createOktaAuth(loaded);
        oktaRef.current = oktaAuth;
        setConfig(loaded);

        // From here on every API call refreshes the token first, as the old app's
        // generateHeaderDetails did (utils.js:50-58).
        setTokenResolver(() => validateAccessToken(oktaAuth, loaded));

        /* -------- Path 1: ?jwt= deep link from Empower (App.js:45-62) -------- */
        const params = new URL(document.location.href).searchParams;

        if (params.has('jwt')) {
          acceptInjectedJwt(params.get('jwt') ?? '');
          const user = getLoggedUserDetails();
          if (cancelled) return;

          setIsAuthenticated(true);
          setIsLoading(false);
          setLoggedUserDetailsState(user);
          loadDelegatedUsers(user?.email);
          return;
        }

        /* -------- Path 2/3: Okta redirect, with the debug override -------- */
        const loggedIn = await initLogin(oktaAuth, loaded);
        if (cancelled) return;
        // initLogin returning false means it has started a redirect; the old app simply
        // fell out of componentDidMount and left isLoading true until the browser left.
        if (!loggedIn) return;

        const accessToken = await getToken(oktaAuth, loaded);
        if (cancelled) return;
        setAccessToken(accessToken);

        const idToken = getIdToken();
        // App.js:72 passed localStorage's idToken straight to jwt-decode, which throws
        // when absent. Preserved — an authenticated session with no identity is an error
        // state, not something to render past.
        if (!idToken) throw new Error('No id token in storage after login');
        setLoggedUserDetails(idToken);

        if (loaded.debug.isDebugEnable) {
          const employee = params.get('employee');
          const userName = params.get('user-name');

          if (employee || userName) {
            // App.js:81-93. Note the original assigned each independently, so a URL with
            // only ?user-name= yields displayName null — reproduced by not defaulting.
            setIsAuthenticated(true);
            setIsLoading(false);
            setLoggedUserDetailsState({
              displayName: employee as string,
              email: userName as string,
            });
            loadDelegatedUsers(userName);
          } else {
            /*
             * App.js:95-97. The debug default user is set WITHOUT isAuthenticated or
             * isLoading, and WITHOUT loading delegated users — so the app renders the
             * main menu (which keys off loggedUserDetails !== null) but with employee
             * numbers still 0, and every data fetch stays behind its `> 0` guard. That
             * is what a debug-enabled environment with no override params does today.
             * Reproduced exactly rather than "fixed".
             */
            setLoggedUserDetailsState(loaded.debug.defaultUser);
          }
        } else {
          const user = getLoggedUserDetails();
          setIsAuthenticated(true);
          setIsLoading(false);
          setLoggedUserDetailsState(user);
          loadDelegatedUsers(user?.email);
        }
      } catch (err) {
        if (cancelled) return;
        setError(toErrorState(err));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loadDelegatedUsers]);

  const selectDelegatedUser = useCallback((operational: number, logged: number) => {
    setOperationalUserGteEmployeeNo(operational);
    setLoggedUserGteEmployeeNo(logged);
  }, []);

  const logIn = useCallback(() => {
    if (oktaRef.current && config) oktaLogin(oktaRef.current, config);
  }, [config]);

  /**
   * App.js:134-138 handleLogOut only cleared `loggedUserDetails`, which unmounts
   * MainMenu — the actual Okta sign-out lived in NavBar, which called
   * `new AuthService().logout()` itself. Both halves are here so a caller cannot do one
   * without the other.
   */
  const logOut = useCallback(() => {
    setLoggedUserDetailsState(null);
    resetTokenResolver();
    if (oktaRef.current) oktaLogout(oktaRef.current);
  }, []);

  const clearError = useCallback(() => setError(NO_ERROR), []);

  return (
    <AuthContext.Provider
      value={{
        config,
        oktaAuth: oktaRef.current,
        isAuthenticated,
        isLoading,
        loggedUserDetails,
        delegatedUsers,
        loggedUserGteEmployeeNo,
        operationalUserGteEmployeeNo,
        error,
        clearError,
        selectDelegatedUser,
        logIn,
        logOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) {
    throw new Error('useAuth must be used inside an AuthProvider');
  }
  return value;
}
